import { ThemeProvider } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectTenantInfo } from '@sophocles/features/tenant/slice';
import useUserPreference from '@sophocles/hooks/useUserPreference';
import { useStatusBarSync } from '@sophocles/platform/useStatusBarSync';

import { createOutdoorlinkTheme } from './index';

// Keyed in the existing user-preferences API alongside the
// modern/classic template selector (`app.customization.theme`).  The
// hook backs the value with sessionStorage so re-renders never
// flash the default while the API request is in flight.
const PREF_KEY = 'app.customization.colorScheme';
const VALID_PREFERENCES = ['light', 'dark', 'system'];

// Default to explicit light so users on a dark-mode OS don't get
// flipped into a dark UI on first load without opting in.  They can
// still choose 'system' (auto-follow OS) or 'dark' from the toggle.
const DEFAULT_PREFERENCE = 'light';

const ThemeSchemeContext = createContext({
  preference: DEFAULT_PREFERENCE,
  scheme: 'light',
  setPreference: () => {},
});

const readSystemScheme = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const ThemeSchemeProvider = ({ children }) => {
  // Backed by the user-preferences API (same backend table as the
  // modern/classic template selector), with a sessionStorage cache
  // for instant initial reads.  Falls back to DEFAULT_PREFERENCE
  // (light) when the user is unauthenticated or the API is offline.
  const tenantInfo = useSelector(selectTenantInfo);
  const [rawPreference, setRawPreference] = useUserPreference(PREF_KEY, DEFAULT_PREFERENCE);
  const preference = VALID_PREFERENCES.includes(rawPreference) ? rawPreference : DEFAULT_PREFERENCE;
  const [systemScheme, setSystemScheme] = useState(readSystemScheme);

  // Track the OS-level preference so 'system' resolves correctly.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = e => setSystemScheme(e.matches ? 'dark' : 'light');
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);

  const scheme = preference === 'system' ? systemScheme : preference;

  // Mirror the RESOLVED scheme to <html data-scheme="…"> so CSS vars
  // can swap without rebuilding the MUI theme — and so legacy SCSS
  // can also opt in via [data-scheme="dark"] selectors.  Always set
  // it explicitly (even for `system`) so the prefers-color-scheme
  // media query is never the source of truth — that lets us ship
  // 'light' as the default for users on a dark OS who haven't
  // explicitly opted in.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-scheme', scheme);
  }, [scheme]);

  const setPreference = useCallback(
    value => {
      const next = VALID_PREFERENCES.includes(value) ? value : DEFAULT_PREFERENCE;
      setRawPreference(next);
    },
    [setRawPreference],
  );

  // Rebuild the MUI theme whenever the color scheme or the tenant brand
  // colors change. The slice starts with the baked brand seed and replaces it
  // with backend values after /api/tenant/ resolves; neutral chrome stays fixed.
  const primaryColor = tenantInfo?.primary_color || null;
  const secondaryColor = tenantInfo?.secondary_color || null;
  const theme = useMemo(
    () => createOutdoorlinkTheme({ scheme, primaryColor, secondaryColor }),
    [scheme, primaryColor, secondaryColor],
  );

  // Push the active scheme down to the native status bar (Capacitor
  // only; no-op on web).  The hook reads the resolved CSS var
  // `--color-statusbar-bg` so it follows the active neutral scheme.
  useStatusBarSync(scheme);

  const ctx = useMemo(() => ({ preference, scheme, setPreference }), [preference, scheme, setPreference]);

  return (
    <ThemeSchemeContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeSchemeContext.Provider>
  );
};

ThemeSchemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useColorScheme = () => useContext(ThemeSchemeContext);
